import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Layout from '../../components/Layout.jsx'
import { LeadsFilters } from '../../components/Filters.jsx'
import { SearchInput } from '../../components/UX.jsx'
import { fmtBRL, fmtPct } from '../../lib/format.js'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import LeadModal from './LeadModal.jsx'
import LeadsKanban from './LeadsKanban.jsx'
import { useCollapseSidebarOnOverlap } from '../../hooks/useSidebarOverlap.js'

function buildInitialFilters() {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 7)

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
    account_id: '',
  }
}

export default function LeadAnalysis() {
  const { toast, activeClient } = useApp()
  const activeClientId = activeClient?.id ?? null

  // Recolhe a sidebar só se o calendário/conteúdo não couber com ela aberta.
  useCollapseSidebarOnOverlap(1100)

  const [leads, setLeads] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    won: 0,
    lost: 0,
    qualified: 0,
    total_won_value: 0,
  })
  const [loading, setLoading] = useState(true)
  const [selectedLead, setSelectedLead] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState(buildInitialFilters)

  // Fase 5 (B1): protege contra stale results em troca rápida de cliente.
  // Mesmo padrão de versioning usado em useApiData. `loadLeads` aqui é local
  // e não passa pelo useApiData, então precisamos da proteção manualmente.
  const genRef = useRef(0)

  const loadLeads = useCallback(
    async (activeFilters) => {
      const myGen = ++genRef.current
      try {
        setLoading(true)
        const [leadsData, statsData] = await Promise.all([
          api.getLeads({ ...activeFilters, limit: 5000 }),
          api.getLeadStats(activeFilters),
        ])
        if (myGen !== genRef.current) return
        setLeads(leadsData || [])
        setStats(
          statsData || {
            total: 0,
            active: 0,
            won: 0,
            lost: 0,
            qualified: 0,
            total_won_value: 0,
          }
        )
      } catch (err) {
        if (myGen !== genRef.current) return
        console.error('Erro ao carregar leads:', err)
        toast('Erro ao carregar leads', 'error')
      } finally {
        if (myGen === genRef.current) setLoading(false)
      }
    },
    [toast]
  )

  // Fase 5 (B1): troca de cliente invalida ad_account selecionada no filtro
  // (pode pertencer a outro cliente) e fecha o modal de lead aberto (lead.id
  // pode não existir no novo client_id → backend retorna 404).
  useEffect(() => {
    if (activeClientId === null) return
    setSelectedLead(null)
    setFilters((prev) => (prev.account_id ? { ...prev, account_id: '' } : prev))
  }, [activeClientId])

  useEffect(() => {
    void loadLeads(filters)
    // activeClientId entra nos deps para refetchar em troca de cliente.
  }, [filters, loadLeads, activeClientId])

  const handleApplyFilters = (nextFilters) => {
    setFilters(nextFilters)
  }

  const filteredLeads = useMemo(() => {
    if (!searchTerm) return leads
    const query = searchTerm.toLowerCase()
    return leads.filter((lead) => {
      return (
        (lead.name || '').toLowerCase().includes(query) ||
        (lead.email || '').toLowerCase().includes(query) ||
        (lead.phone || '').toLowerCase().includes(query) ||
        (lead.location || '').toLowerCase().includes(query) ||
        (lead.keyword || '').toLowerCase().includes(query)
      )
    })
  }, [leads, searchTerm])

  const totalPipelineValue = useMemo(
    () =>
      leads
        .filter((lead) => !lead.outcome)
        .reduce((sum, lead) => sum + (lead.estimated_value || 0), 0),
    [leads]
  )

  const wonValue = useMemo(
    () =>
      leads
        .filter((lead) => lead.outcome === 'ganho')
        .reduce((sum, lead) => sum + (lead.estimated_value || 0), 0),
    [leads]
  )

  const refreshStats = useCallback(async () => {
    const statsData = await api.getLeadStats(filters)
    setStats(statsData)
  }, [filters])

  const handleStageChange = async (id, nextData) => {
    const nextStage = typeof nextData === 'string' ? nextData : nextData?.stage
    if (!nextStage) return

    const lead = leads.find((item) => item.id === id)
    const payload = { stage: nextStage }
    if (lead?.created_at) {
      payload.created_at_orig = lead.created_at
    }

    setLeads((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, stage: nextStage, status: nextStage, outcome: null }
          : item
      )
    )

    try {
      await api.updateLead(id, payload)
      await refreshStats()
      if (nextStage === 'qualificado') {
        toast('Lead movido para Qualificado', 'success')
      }
    } catch (err) {
      toast(err?.message || 'Erro ao mover lead', 'error')
      await loadLeads(filters)
    }
  }

  const handleOutcomeChange = async (id, outcome) => {
    const lead = leads.find((item) => item.id === id)
    if (!lead) return

    if (outcome === 'ganho') {
      if (!(lead.estimated_value > 0)) {
        toast('Defina o valor do negócio antes de marcar como ganho', 'error')
        return
      }
      if ((lead.stage || lead.status) !== 'venda') {
        toast('Ganho so pode ser aplicado a leads em Venda', 'error')
        return
      }
    }

    const payload = { outcome }
    if (lead.created_at) {
      payload.created_at_orig = lead.created_at
    }

    setLeads((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, outcome, status: outcome || item.stage }
          : item
      )
    )

    try {
      await api.updateLead(id, payload)
      await refreshStats()
      if (outcome === 'ganho') {
        toast('Negocio fechado com sucesso', 'success')
      } else if (outcome === 'perdido') {
        toast('Lead marcado como perdido', 'info')
      } else {
        toast('Lead reativado no pipeline', 'info')
      }
    } catch (err) {
      toast(err?.message || 'Erro ao atualizar lead', 'error')
      await loadLeads(filters)
    }
  }

  const handleStatusChange = (id, nextData) => {
    if (typeof nextData === 'string' || nextData?.stage !== undefined) {
      return handleStageChange(id, nextData)
    }
    if (nextData?.outcome !== undefined) {
      return handleOutcomeChange(id, nextData.outcome)
    }
    return Promise.resolve()
  }

  const handleLeadSave = async (updatedLead) => {
    const payload = { ...updatedLead }
    if (updatedLead.created_at) {
      payload.created_at_orig = updatedLead.created_at
    }

    setLeads((prev) =>
      prev.map((item) => (item.id === updatedLead.id ? { ...item, ...updatedLead } : item))
    )

    try {
      await api.updateLead(updatedLead.id, payload)
      await refreshStats()
    } catch (err) {
      toast(err?.message || 'Erro ao sincronizar lead', 'error')
      await loadLeads(filters)
    }
  }

  return (
    <Layout
      title="Google Ads — Leads"
      breadcrumb={[{ label: 'Marketing' }, { label: 'Google Ads', to: '/google-ads' }, { label: 'Leads' }]}
    >
      <div className="money-bar">
        <div className="money-item">
          <span className="t-label">Pipeline ativo</span>
          <span className="money-val">{fmtBRL(totalPipelineValue)}</span>
        </div>
        <div className="money-divider" />
        <div className="money-item">
          <span className="t-label">Leads qualificados</span>
          <span className="money-val">{stats.qualified || 0}</span>
        </div>
        <div className="money-divider" />
        <div className="money-item">
          <span className="t-label">Ganhos no período</span>
          <span className="money-val highlight">{fmtBRL(wonValue)}</span>
        </div>
        <div className="money-divider" />
        <div className="money-item">
          <span className="t-label">Taxa de conversão</span>
          <span className="money-val">
            {stats.total > 0 ? fmtPct((stats.won / stats.total) * 100) : '—'}
          </span>
        </div>
      </div>

      <div className="ga-filters-bar" style={{ marginTop: 16, marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <SearchInput
          placeholder="Buscar por nome, email, telefone..."
          value={searchTerm}
          onChange={setSearchTerm}
          style={{ marginLeft: 'auto' }}
        />
      </div>

      <LeadsFilters
        filters={filters}
        onChange={setFilters}
        onApply={handleApplyFilters}
        onClear={() => handleApplyFilters({ startDate: '', endDate: '', account_id: '' })}
      />

      {loading ? (
        <div className="kanban-skeleton">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div key={item} className="kbc-col-skeleton">
              <div className="skeleton-head" />
              <div className="skeleton-card" />
              <div className="skeleton-card" />
            </div>
          ))}
        </div>
      ) : (
        <LeadsKanban
          leads={filteredLeads}
          onLeadClick={setSelectedLead}
          onStatusChange={handleStatusChange}
        />
      )}

      {selectedLead ? (
        <LeadModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={handleStatusChange}
          onSave={handleLeadSave}
        />
      ) : null}
    </Layout>
  )
}
